
import Vue from 'vue'
import Input from '@/components/atoms/Input.vue'
import TextArea from '@/components/atoms/TextArea.vue'
import FormButtons from '@/components/userProfile/FormButtons.vue'
import ActionToast from '@/components/atoms/ActionToast.vue'
import Select from '@/components/atoms/Select.vue'
import TrashIcon from '@/assets/svgs/trash_icon.svg?inline'
import AddIcon from '@/assets/svgs/add_icon.svg?inline'
import ISO from 'iso-639-1'
import { FETCH_WORK_EDUCATION, UPDATE_PERSONAL_INFO } from '@/graphql.js'
import { UserProfile, WorkExperience, EducationItem, Degree, State, Month } from 'vue/types/vue'
import STATE_ISO_MAP from '@/plugins/states'
import MONTH_ENUM_MAP from '@/plugins/months'
import DEGREE_ENUM_MAP from '@/plugins/degrees'

const RADIX_REGEX = /\B(?=(\d{3})+(?!\d))/g

interface EducationFormItem {
  id: string | null;
  degreeName: string;
  institution: string;
  city: string;
  stateName: string;
  graduationYear: string;
}

interface WorkExperienceFormItem {
  id: string | null;
  title: string;
  companyName: string;
  city: string;
  stateName: string;
  currentlyWorking: boolean;
  startMonthName: string;
  startYear: string;
  endMonthName: string;
  endYear: string;
  jobDescription: string;
}

export default Vue.extend({
  name: 'WorkEducation',
  data: () => ({
    userProfile: {} as UserProfile,
    extendedUserProfile: {}, // Contains additional fields not returned by the User Profile service
    savedSuccessfully: false,
    saveError: false,
    candidateWorkExperienceToBeRemoved: -1,
    candidateEducationItemToBeRemoved: -1,
    showRequiredFieldsDialog: false,
    showNavigationWarning: false,
    error: '',
    returnedLanguagesAsString: [] as string[],
    form: {
      bio: '',
      workExperiences: [] as WorkExperienceFormItem[],
      educations: [] as EducationFormItem[],
      languages: [] as string[],
    },
    emptyWorkExperience: {
      id: '',
      title: '',
      companyName: '',
      location: {
        city: '',
        stateName: '',
        country: 'US',
      },
      currentlyWorking: false,
      startMonthName: '',
      startYear: '',
      endMonthName: '',
      endYear: '',
      jobDescription: '',
    },
    emptyEducationItem: {
      id: '',
      degreeName: '',
      institution: '',
      location: {
        city: '',
        stateName: '',
        country: 'US',
      },
      graduationYear: '',
    },
    degrees: Object.values(DEGREE_ENUM_MAP).map(item => ({ name: item })),
    months: Object.values(MONTH_ENUM_MAP).map(item => ({ name: item })),
    states: Object.values(STATE_ISO_MAP).map(item => ({ name: item })),
    languages: ISO.getAllNames().map(name => ({ name })),
    maxBioLength: 500,
    maxJobDescriptionLength: 1000,
  }),
  computed: {
    hasEdits (): boolean {
      return (
        JSON.stringify(this.form) !== JSON.stringify(this.extendedUserProfile)
      )
    },
    lastOneHundredYears: function () {
      let currentYear = new Date().getFullYear()
      let endYear = currentYear - 100
      return Array.from(
        { length: currentYear - endYear + 1 },
        (value, i) => currentYear - i
      ).map((year) => ({ name: year.toString() }))
    },
    startMonthsForCurrentYear () {
      // Returns list of allowed start months for current year
      // ex: If it is currently May, returns Jan - May
      return this.months.slice(0, new Date().getMonth() + 1)
    },
  },
  components: {
    ActionToast,
    AddIcon,
    FormButtons,
    Select,
    Input,
    TextArea,
    TrashIcon,
  },
  watch: {
    userProfile: function (value) {
      this.setDataFromProfile(value)
    },
  },
  apollo: {
    userProfile: {
      query: FETCH_WORK_EDUCATION,
      variables: function () {
        if (this.$route.params?.masqueradeUser) {
          return { input: this.$route.params?.masqueradeUser }
        }
      },
      update: (data) => data.user as UserProfile,
    },
  },
  methods: {
    removeWorkExperience: function (index: number) {
      this.form.workExperiences.splice(index, 1)
      this.candidateWorkExperienceToBeRemoved = -1
    },
    removeEducationItem: function (index: number) {
      this.form.educations.splice(index, 1)
      this.candidateEducationItemToBeRemoved = -1
    },
    setDataFromProfile: function (profile: UserProfile) {
      let { workExperiences, educations, bio, languages } = profile
      let workExperienceFormData: WorkExperienceFormItem[] =
        workExperiences.map((workExp) => ({
          id: workExp.id ?? null,
          title: workExp.title,
          companyName: workExp.companyName,
          city: workExp.city,
          stateName: STATE_ISO_MAP[workExp.stateIsoCode],
          currentlyWorking: !workExp.endMonth || !workExp.endYear,
          startMonthName: MONTH_ENUM_MAP[workExp.startMonth],
          startYear: workExp.startYear,
          endMonthName: workExp.endMonth
            ? MONTH_ENUM_MAP[workExp.endMonth]
            : '',
          endYear: workExp.endYear || '',
          jobDescription: workExp.jobDescription || '',
        }))
      let educationFormData: EducationFormItem[] = educations.map(
        (education) => ({
          id: education.id ?? null,
          degreeName: DEGREE_ENUM_MAP[education.degree],
          institution: education.institution,
          city: education.city,
          stateName: STATE_ISO_MAP[education.stateIsoCode],
          graduationYear: education.graduationYear,
        })
      )
      const formData = {
        bio: bio ?? '',
        workExperiences: workExperienceFormData,
        educations: educationFormData,
        languages: languages.map(code => ISO.getName(code)),
      }
      this.form = JSON.parse(JSON.stringify(formData))
      this.extendedUserProfile = JSON.parse(JSON.stringify(formData))
    },
    cancel: function () {
      // Revert fields
      this.setDataFromProfile(this.userProfile)
    },
    getUserEnteredWorkExperiences: function (): WorkExperience[] {
      // Convert mapped values of enums to enum keys
      return this.form.workExperiences.map((workForm) => ({
        id: workForm.id || undefined,
        title: workForm.title,
        companyName: workForm.companyName,
        city: workForm.city,
        stateIsoCode: Object.keys(STATE_ISO_MAP).find(
          (key) => STATE_ISO_MAP[key] === workForm.stateName
        ) as State,
        startMonth: Object.keys(MONTH_ENUM_MAP).find(
          (key) => MONTH_ENUM_MAP[key] === workForm.startMonthName
        ) as Month,
        startYear: workForm.startYear,
        endMonth: workForm.currentlyWorking
          ? null
          : Object.keys(MONTH_ENUM_MAP).find(
            (key) => MONTH_ENUM_MAP[key] === workForm.endMonthName
          ) as Month,
        endYear: workForm.currentlyWorking ? null : workForm.endYear,
        jobDescription: workForm.jobDescription,
      }))
    },
    getUserEnteredEducationItems: function (): EducationItem[] {
      return this.form.educations.map((eduForm) => ({
        id: eduForm.id || undefined,
        degree: Object.keys(DEGREE_ENUM_MAP).find(
          (key) => (DEGREE_ENUM_MAP[key] === eduForm.degreeName)
        ) as Degree,
        institution: eduForm.institution,
        city: eduForm.city,
        stateIsoCode: Object.keys(STATE_ISO_MAP).find(
          (key) => STATE_ISO_MAP[key] === eduForm.stateName
        ) as State,
        graduationYear: eduForm.graduationYear,
      }))
    },
    save: async function () {
      if (this.hasEdits) {
        this.saveError = false
        try {
          const updates = {
            bio: this.form.bio,
            workExperiences: this.getUserEnteredWorkExperiences(),
            educations: this.getUserEnteredEducationItems(),
            languages: this.form.languages.map(ISO.getCode),
          }
          await this.$apollo.mutate({
            mutation: UPDATE_PERSONAL_INFO,
            variables: { uuid: this.userProfile.uuid, updates: updates },
          })
          this.$apollo.queries.userProfile.refresh()
          this.savedSuccessfully = true
        } catch (e) {
          this.error = (e as Error).message
          this.saveError = true
        }
      }
    },
    requiredFieldIsMissing: function () {
      return (
        !this.form.bio ||
        this.form.workExperiences.some(
          (workExperience) =>
            !workExperience.title ||
            !workExperience.companyName ||
            !workExperience.city ||
            !workExperience.stateName ||
            !workExperience.startMonthName ||
            !workExperience.startYear ||
            (!workExperience.endMonthName &&
              !workExperience.currentlyWorking) ||
            (!workExperience.endYear && !workExperience.currentlyWorking)
        ) ||
        this.form.educations.some(
          (educationItem) =>
            !educationItem.degreeName ||
            !educationItem.institution ||
            !educationItem.city ||
            !educationItem.stateName
        )
      )
    },
    numberToRadixString: function (value: number) {
      return value.toString().replace(RADIX_REGEX, ',')
    },
    addCommasToNumber: function (value: number): string {
      return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    },
    validateStartDate: function (expIdx: number) {
      // Triggers validation on the start date for the work experience at the provided index
      this.$refs.startMonth[expIdx].$refs.qselect.validate()
      this.$refs.startYear[expIdx].$refs.qselect.validate()
    },
    validateEndDate: function (expIdx: number) {
      // Triggers validation on the end date for the work experience at the provided index
      this.$refs.endMonth[expIdx].$refs.qselect.validate()
      this.$refs.endYear[expIdx].$refs.qselect.validate()
    },
    isEndDateValid: function (exp: WorkExperienceFormItem) {
      // If years are not the same, no need to compare months just year ordering
      if (exp.startYear !== exp.endYear) return exp.startYear < exp.endYear

      // Else, need to validate month ordering
      const startMonthIdx = Object.keys(MONTH_ENUM_MAP).findIndex(
        (key) => MONTH_ENUM_MAP[key] === exp.startMonthName
      )
      const endMonthIdx = Object.keys(MONTH_ENUM_MAP).findIndex(
        (key) => MONTH_ENUM_MAP[key] === exp.endMonthName
      )
      return startMonthIdx! <= endMonthIdx!
    },
  },
  beforeRouteLeave (to, from, next) {
    if (this.$route.params?.masqueradeUser) {
      next()
    }
    if (this.hasEdits) {
      this.$emit('hideNavMenu')
      this.showNavigationWarning = true
    } else {
      next()
    }
  },
})
